import { useMemo, useState } from 'react';
import {
  Typography,
  Box,
  Container,
  Button,
  CircularProgress,
  useTheme,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import useNotifications from '../../../_lib/notifications/useNotifications';
import { ProductCode, ServiceStatus, type Product } from '../types';
import {
  useLeadConvertor,
  LeadConvertorError,
  ERR_LEAD_CONVERSION_PERMISSION,
} from '../hooks/useLeadConvertor';
import { useMeQuery } from '../../../components/hooks/useMeQuery';
import { useProductFamilyById } from '../hooks/useProductFamilyById';
import { useProductsByProductFamilyId } from '../hooks/useProductsByProductFamilyId';
import ResilienceLayout from './ResilienceLayout';
import { useCheckAnyPolicyHasStatus } from '../../../components/hooks/queries/policies/useCheckAnyPolicyHasStatus';
import {
  EXPIRED_STATUS,
  RENEWED_STATUS,
} from '../../../console/_statics/policy.statics';
import ProductList from './ProductList';
import ProductReplacementModal from './ProductReplacementModal';
import { useIsEligibleForFreeCtaas } from '../hooks/useIsEligibleForFreeCtaas';
import { useCtaasServiceStatus } from '../hooks/useCtaasServiceStatus';
import { useAuth } from '../../../components/hooks/useAuth';
import { useServiceStatusInfo } from '../hooks/useServiceStatusInfo';
import { useActor } from '../../../components/hooks/useActor';
import { GradientBackground } from './GradientBackground';
import { useCart } from '../features/self-serve/hooks/useCart';
import {
  addCartLineItemToCart,
  createCart,
  deleteCartLineItem,
} from '../features/self-serve/services/cartService';
import { useQueryClient } from '@tanstack/react-query';
import { Cart } from '../features/self-serve/types/types';
import { useAccountDetails } from '../../../components/hooks/queries/accounts/useAccountDetails';
import { useInvoices } from '../features/self-serve/hooks/useInvoices';
import { getCurrentServiceStatusInfo } from '../utils/getCurrentServiceStatusInfo';

const ServicePricing = () => {
  const [processingProductId, setProcessingProductId] = useState<string | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [existingProduct, setExistingProduct] = useState<Product | null>(null);
  const notifications = useNotifications();
  const history = useHistory();
  const location = useLocation();
  const { productFamilyId } = useParams<{ productFamilyId: string }>();
  const { data: me } = useMeQuery();
  const theme = useTheme();
  const accountId = me?.account?.id;
  const actor = useActor();
  const { data: cart } = useCart();
  const queryClient = useQueryClient();
  const { data: account } = useAccountDetails(accountId);

  // Detect if current user is in Pool tenant
  const isPoolTenant = actor.isPool || location.pathname.startsWith('/pool');

  const { data: productFamily } = useProductFamilyById(productFamilyId);
  const { data: products = [], isLoading: isProductsLoading } =
    useProductsByProductFamilyId(productFamilyId);
  const hasExpiredPolicy = useCheckAnyPolicyHasStatus(EXPIRED_STATUS);
  const hasRenewedPolicy = useCheckAnyPolicyHasStatus(RENEWED_STATUS);

  // Call useServiceStatusInfo for all tenants - the hook will handle Pool tenant logic internally
  const {
    data: serviceStatusInfos = [],
    isLoading: isServiceStatusInfoLoading,
  } = useServiceStatusInfo();

  const isEligibleForFreeCtaas = useIsEligibleForFreeCtaas();
  const ctaasServiceStatus = useCtaasServiceStatus();
  const { isCowbellUserJumped } = useAuth();

  const leadConvertor = useLeadConvertor(isPoolTenant);
  const { data: invoices = [] } = useInvoices();

  const activeProducts = useMemo(() => {
    if (Array.isArray(products) && products.length === 0) {
      return [];
    }

    return products
      .filter((product) => {
        if (product.active) {
          return true;
        }

        const currentServiceStatusInfo = getCurrentServiceStatusInfo(
          product,
          serviceStatusInfos
        );

        if (!currentServiceStatusInfo) {
          return false;
        }

        return ![
          ServiceStatus.NotActivated,
          ServiceStatus.RequestReceived,
        ].includes(currentServiceStatusInfo.status);
      })
      .sort((a, z) => a.order - z.order);
  }, [products, serviceStatusInfos]);

  const handleAddProductToCart = async (
    cart: Cart | null,
    product: Product,
    quantity: number,
    hasConfirmedReplacement: boolean
  ) => {
    try {
      setProcessingProductId(product.id);

      leadConvertor.mutate({ productFamily, product }); // fire-and-forget

      if (!cart) {
        await createCart({
          data: { productId: product.id, quantity, isoLanguageCode: 'en' },
        });

        notifications.success('Cart created and product added to cart');
        queryClient.invalidateQueries({ queryKey: [useCart.queryKey] });
        return;
      }

      // if any of product family products added in to cart then first show modal to confirm replacement
      const existingCartItem = cart.cartItems.find(
        (item) => item.productFamily.id === productFamily.id
      );

      if (existingCartItem && !hasConfirmedReplacement) {
        setSelectedProduct(product);
        setSelectedQuantity(quantity);
        setExistingProduct(existingCartItem.product);
        setIsModalOpen(true);
        return;
      }

      // If user confirmed replacement, remove existing items first
      if (existingCartItem && hasConfirmedReplacement) {
        await deleteCartLineItem(existingCartItem.id);
      }

      await addCartLineItemToCart({
        data: { productId: product.id, quantity, isoLanguageCode: 'en' },
      });

      // Reset quantity to 1
      setSelectedQuantity(1);
      notifications.success('Product added to cart');

      await queryClient.invalidateQueries({ queryKey: [useCart.queryKey] });
    } catch (error) {
      console.error('Error during add product to cart process', error);
      notifications.error(
        'An error occurred while adding product to cart. Please try again later.'
      );
    } finally {
      setProcessingProductId(null);
    }
  };

  const handleAddProductToOpportunity = async (product: Product) => {
    try {
      if (isCowbellUserJumped) {
        throw new LeadConvertorError(ERR_LEAD_CONVERSION_PERMISSION);
      }

      setProcessingProductId(product.id);

      if (!productFamily) {
        throw new LeadConvertorError(
          "Couldn't find the product family details"
        );
      }

      if (!product) {
        throw new LeadConvertorError('Please select a plan to proceed');
      }

      await leadConvertor.mutateAsync({ productFamily, product });

      if (product.code === ProductCode.CTS_BST && isEligibleForFreeCtaas) {
        history.push({
          pathname: '/customer/training',
          state: { isServiceActivated: true, productCode: product.code },
        });
        return;
      }

      history.push({
        pathname: '/customer/resilience/services',
        state: {
          isServiceActivated: true,
          productCode: product.code,
        },
      });
    } catch (error) {
      const userInfo = {
        accountId,
        name: `${me?.firstName} ${me?.lastName}`,
        email: me?.email,
        product,
        error,
      };

      console.error('Error during checkout process', userInfo);

      if (error instanceof Error) {
        const errorProcessingMessage =
          'An error occurred while processing your request. Please try again later.';

        notifications.show({
          type: 'error',
          title:
            error.name === 'LeadConvertorError'
              ? error.message
              : errorProcessingMessage,
          position: 'top-right',
        });
      }
    } finally {
      setProcessingProductId(null);
    }
  };

  const handleConfirmReplacement = async () => {
    if (!selectedProduct) {
      return;
    }

    setIsModalOpen(false);
    await handleAddProductToCart(cart, selectedProduct, selectedQuantity, true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
    setSelectedQuantity(1);
    setExistingProduct(null);
    setProcessingProductId(null);
  };

  if (isProductsLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (Array.isArray(activeProducts) && activeProducts.length === 0) {
    return (
      <Container>
        <Typography
          variant="h5"
          gutterBottom
          fontWeight={700}
          color={theme.palette.common.darkGrey5}
        >
          No plans available at the moment
        </Typography>
      </Container>
    );
  }

  return (
    <GradientBackground>
      <ResilienceLayout>
        <Box
          sx={{
            minHeight: '100vh',
            width: '100%',
            padding: '1rem',
          }}
        >
          <Box
            display="flex"
            justifyContent="flex-start"
            sx={{ marginBottom: '1rem' }}
          >
            <Button
              variant="text"
              onClick={() => history.goBack()}
              startIcon={<ChevronLeftIcon />}
              sx={{
                color: theme.palette.common.darkGrey5,
                '&:hover': { color: theme.palette.primary.cowbellBlue },
              }}
            >
              Back
            </Button>
          </Box>
          <Box
            display="grid"
            gridTemplateColumns="0.75fr"
            justifyItems="center"
            sx={{
              margin: '2rem 0px',
              textAlign: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="h6"
              fontSize="2rem"
              color={theme.palette.common.azureBlue}
              sx={{ fontWeight: 700 }}
            >
              {productFamily?.planHeading ?? `${productFamily?.name} Packages`}
            </Typography>
            <Typography
              variant="body1"
              fontSize="1.25rem"
              color={theme.palette.pricingCardGradient.subheadingColor}
              sx={{
                maxWidth: '48rem',
                margin: '0 auto',
              }}
            >
              {productFamily?.planSubHeading ??
                `Find the ideal plan tailored to your needs and goals`}
            </Typography>
          </Box>
          <Box
            sx={{
              width: '100%',
              padding: '2rem',
              backgroundColor: 'transparent',
              marginBottom: '4rem',
            }}
          >
            <ProductList
              products={activeProducts}
              processingProductId={processingProductId}
              hasExpiredPolicy={hasExpiredPolicy}
              hasRenewedPolicy={hasRenewedPolicy}
              serviceStatusInfos={serviceStatusInfos}
              isServiceStatusInfoLoading={isServiceStatusInfoLoading}
              productFamily={productFamily}
              isPoolTenant={isPoolTenant}
              cart={cart}
              ctaasServiceStatus={ctaasServiceStatus}
              isEligibleForFreeCtaas={isEligibleForFreeCtaas}
              handleAddProductToCart={handleAddProductToCart}
              handleAddProductToOpportunity={handleAddProductToOpportunity}
              revenue={account?.revenue}
              invoices={invoices}
            />
          </Box>
        </Box>
      </ResilienceLayout>

      <ProductReplacementModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmReplacement}
        isLoading={processingProductId === selectedProduct?.id}
        selectedProduct={selectedProduct}
        existingProduct={existingProduct}
        productFamily={productFamily}
      />
    </GradientBackground>
  );
};

export default ServicePricing;
